import React, { useEffect, useRef } from 'react';

const OceanBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameId = useRef<number>(0);
  
  // Sea creatures - moved outside useEffect
  const seaCreatures = useRef<{
    x: number;
    y: number;
    speed: number;
    type: string;
    size: number;
    direction: number;
  }[]>([]);
  
  // Bubbles - moved outside useEffect
  const bubbles = useRef<{
    x: number;
    y: number;
    speed: number;
    size: number;
    opacity: number;
  }[]>([]);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas dimensions
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initSeaLife();
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Animation variables
    let time = 0;
    
    // Initialize sea life
    function initSeaLife() {
      seaCreatures.current = [];
      bubbles.current = [];
      
      // Create sea creatures
      const creatures = ['🐠', '🐟', '🦈', '🐙', '🦞', '🐢', '🦑'];
      for (let i = 0; i < 4; i++) {
        seaCreatures.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * (canvas.height * 0.6) + canvas.height * 0.2,
          speed: Math.random() * 0.5 + 0.2,
          type: creatures[Math.floor(Math.random() * creatures.length)],
          size: Math.random() * 15 + 15,
          direction: Math.random() > 0.5 ? 1 : -1
        });
      }
      
      // Create bubbles
      for (let i = 0; i < 12; i++) {
        bubbles.current.push({
          x: Math.random() * canvas.width,
          y: canvas.height + Math.random() * 100,
          speed: Math.random() * 0.5 + 0.3,
          size: Math.random() * 8 + 4,
          opacity: Math.random() * 0.6 + 0.2
        });
      }
    }
    
    // Create wave layers with different properties
    const waves = [
      {
        amplitude: 35,
        frequency: 0.01,
        speed: 0.3,
        direction: 1, // Moving right
        color: 'rgba(8, 145, 178, 0.5)', // Deep cyan
        xOffset: 0
      },
      {
        amplitude: 25,
        frequency: 0.02,
        speed: 0.2,
        direction: -1, // Moving left
        color: 'rgba(14, 165, 233, 0.4)', // Mid blue
        xOffset: 100
      },
      {
        amplitude: 20,
        frequency: 0.03,
        speed: 0.1,
        direction: 1, // Moving right
        color: 'rgba(125, 211, 252, 0.3)', // Light blue
        xOffset: 200
      },
      {
        amplitude: 15,
        frequency: 0.04,
        speed: 0.05,
        direction: -1, // Moving left
        color: 'rgba(186, 230, 253, 0.2)', // Very light blue
        xOffset: 300
      }
    ];
    
    // Animation loop
    function animate() {
      // Clear the entire canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Increment time for animation
      time += 0.02;
      
      // Draw underwater gradient background
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, 'rgba(56, 189, 248, 0.85)');
      gradient.addColorStop(0.55, 'rgba(8, 145, 178, 0.9)');
      gradient.addColorStop(1, 'rgba(2, 80, 110, 0.95)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw and update sea creatures
      seaCreatures.current.forEach(creature => {
        ctx.font = `${creature.size}px sans-serif`;
        ctx.fillStyle = `rgba(255, 255, 255, 0.8)`;
        
        // Add slight bobbing motion
        const bobY = Math.sin(time * 2 + creature.x * 0.01) * 3;
        ctx.fillText(creature.type, creature.x, creature.y + bobY);
        
        // Move creature
        creature.x += creature.speed * creature.direction;
        
        // Wrap around screen
        if (creature.direction > 0 && creature.x > canvas.width + 50) {
          creature.x = -50;
        } else if (creature.direction < 0 && creature.x < -50) {
          creature.x = canvas.width + 50;
        }
      });
      
      // Draw and update bubbles
      bubbles.current.forEach(bubble => {
        ctx.beginPath();
        ctx.arc(bubble.x, bubble.y, bubble.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(173, 216, 230, ${bubble.opacity})`;
        ctx.fill();
        
        // Add slight horizontal drift
        bubble.x += Math.sin(time * 1.5 + bubble.y * 0.01) * 0.2;
        bubble.y -= bubble.speed;
        
        // Reset bubble when it reaches the top
        if (bubble.y < -20) {
          bubble.y = canvas.height + Math.random() * 100;
          bubble.x = Math.random() * canvas.width;
          bubble.opacity = Math.random() * 0.6 + 0.2;
        }
      });
      
      // Draw waves from bottom to top
      for (let i = waves.length - 1; i >= 0; i--) {
        drawWave(waves[i], time);
      }
      
      animationFrameId.current = requestAnimationFrame(animate);
    }
    
    // Draw a single wave
    function drawWave(wave: {
      amplitude: number;
      frequency: number;
      speed: number;
      direction: number;
      color: string;
      xOffset: number;
    }, time: number) {
      const { amplitude, frequency, speed, direction, color, xOffset } = wave;
      
      ctx.beginPath();
      
      // Start from a higher point on the canvas
      ctx.moveTo(0, canvas.height * 0.7);
      
      // Create dynamic, moving wave pattern with directional movement
      for (let x = 0; x <= canvas.width; x += 5) {
        // Multiple sine waves for complex movement
        const y = 
          Math.sin((x + xOffset + direction * time * speed * 100) * frequency) * amplitude +
          Math.sin((x + xOffset + direction * time * speed * 150) * (frequency * 1.5)) * (amplitude * 0.3) +
          Math.sin((x + xOffset + direction * time * speed * 200) * (frequency * 0.5)) * (amplitude * 0.1);
        
        // Position waves anchored to the bottom of the screen
        ctx.lineTo(x, canvas.height * 0.7 - y);
      }
      
      // Ensure last point is drawn
      ctx.lineTo(canvas.width, canvas.height * 0.7);
      
      // Complete the path to fill the area beneath the wave
      ctx.lineTo(canvas.width, canvas.height);
      ctx.lineTo(0, canvas.height);
      ctx.closePath();
      
      // Fill with semi-transparent color
      ctx.fillStyle = color;
      ctx.fill();
    }
    
    initSeaLife();
    animate();
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId.current);
    };
  }, []);
  
  return (
    <canvas 
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ 
        backgroundColor: 'transparent',
        willChange: 'transform' // Performance optimization
      }}
    />
  );
};

export default OceanBackground;